import { useCallback, useEffect, useState } from "react";

/* ── APPEARANCE: THEME AND TEXT SIZE ─────────────────────────────────────────────────────
 * Two settings the doctor owns, stored in this browser and applied to the document root.
 *
 * Text size works by moving the ROOT font size, not by restyling screens. Tailwind's sizes
 * and spacing are in rem, so one number moves type, padding and gaps together and the layout
 * keeps its proportions — the difference between a bigger screen and a screen with bigger
 * letters crammed into the same boxes. It also means a screen built after today inherits the
 * setting without knowing it exists.
 *
 * 16px is the browser's own default and stays ours: a doctor who never opens Settings sees
 * exactly what they see now. The other steps are deliberately few. A slider invites fiddling
 * with a number nobody can judge in the abstract; four named sizes can be tried in a second
 * each. Browser zoom (Ctrl/Cmd +) still works on top of this and is still the bigger hammer. */

export const DARK_KEY = "darkMode";
export const FONT_KEY = "fontScale";

export const FONT_SIZES = [
  { key: "small", label: "Small", px: 15, hint: "Compact — more on screen at once" },
  { key: "default", label: "Default", px: 16, hint: "The standard size" },
  { key: "large", label: "Large", px: 18, hint: "Easier to read at arm's length" },
  { key: "xlarge", label: "Extra large", px: 20, hint: "Largest — least scrolling-free space" },
];

const DEFAULT_FONT = "default";

function sizeFor(key) {
  return FONT_SIZES.find((s) => s.key === key) || FONT_SIZES.find((s) => s.key === DEFAULT_FONT);
}

/* localStorage throws rather than returning null in a locked-down browser (private mode,
   blocked site data). An unreadable preference is not worth a blank portal. */
function readStored(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

function writeStored(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* preference simply does not survive this session */
  }
}

export function readDark() {
  return readStored(DARK_KEY, "false") === "true";
}

export function readFontSize() {
  return sizeFor(readStored(FONT_KEY, DEFAULT_FONT)).key;
}

export function applyDark(on) {
  const root = document.documentElement;
  if (on) root.classList.add("dark");
  else root.classList.remove("dark");
}

export function applyFontSize(key) {
  document.documentElement.style.fontSize = `${sizeFor(key).px}px`;
}

/* Called from index.js BEFORE React renders. Applying these in an effect instead would show
   every doctor one frame of the wrong theme and the wrong size on every page load — the
   white flash that makes a dark-mode app feel broken. */
export function applyStoredAppearance() {
  applyDark(readDark());
  applyFontSize(readFontSize());
}

/* Both the top bar's theme button and the Settings page change the same two values, so they
   cannot hold private copies of them: whichever was not clicked would go stale and then
   overwrite the other. A window event is the whole synchronisation — these are two booleans
   read on mount, not state worth a store. */
const EVENT = "cuore:appearance";

export function useAppearance() {
  const [dark, setDarkState] = useState(readDark);
  const [fontSize, setFontSizeState] = useState(readFontSize);

  useEffect(() => {
    const sync = () => {
      setDarkState(readDark());
      setFontSizeState(readFontSize());
    };
    window.addEventListener(EVENT, sync);
    // `storage` fires in OTHER tabs — a doctor with the record open in a second window gets
    // the same theme there without reloading it.
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setDark = useCallback((on) => {
    const next = typeof on === "function" ? on(readDark()) : !!on;
    writeStored(DARK_KEY, next);
    applyDark(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  const setFontSize = useCallback((key) => {
    const next = sizeFor(key).key;
    writeStored(FONT_KEY, next);
    applyFontSize(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return { dark, setDark, fontSize, setFontSize };
}
